import { useState } from "react";
import { Link } from "react-router-dom";
import { Tag, ShoppingBag, CalendarRange, Sparkles, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, errMsg } from "../lib/api";
import { Countdown } from "./Marketplace";
import { useAuth } from "../context/AuthContext";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";

export function DealsSection({ deals }) {
  if (!deals || deals.length === 0) return null;
  const active = deals.filter((d) => d.status !== "expired");
  if (active.length === 0) return null;
  const sorted = [...active].sort((a, b) => (b.featured === true) - (a.featured === true));
  return (
    <section data-testid="venue-deals">
      <div className="flex items-center justify-between mb-5">
        <p className="overline flex items-center gap-2"><Sparkles size={14} /> Limited-time deals</p>
        <Link to="/deals" className="text-xs text-rose-400 hover:underline">All deals →</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((d) => <DealCard key={d.id} d={d} />)}
      </div>
    </section>
  );
}

function DealCard({ d }) {
  const { user } = useAuth();
  const [claimed, setClaimed] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "" });
  const soldOut = d.status === "sold_out";
  const scheduled = d.status === "scheduled";
  const isPromo = !!d.promo_code;
  const left = d.quantity_available ? d.quantity_available - d.quantity_claimed : null;
  const claim = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    try { const { data } = await api.post(`/deals/${d.id}/claim`, f); setClaimed(data.code); toast.success("Deal claimed!"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copyPromo = async () => {
    try { await navigator.clipboard.writeText(d.promo_code); setCopied(true); toast.success("Promo code copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select the code manually"); }
  };
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#111] border border-rose-500/30" data-testid={`deal-${d.id}`}>
      {d.image && <img src={fileUrl(d.image)} alt="" className="w-full aspect-square object-cover" />}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold text-base leading-tight">{d.title}</h3>
          {d.discount_percentage && <span className="shrink-0 bg-rose-500 text-white font-black text-xs rounded-lg px-2 py-0.5">-{d.discount_percentage}%</span>}
        </div>
        {isPromo && <span className="mt-2 inline-flex items-center gap-1 bg-rose-500/15 text-rose-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"><Tag size={10} /> Promo Code</span>}
        {d.description && <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{d.description}</p>}
        <div className="flex items-end gap-2 mt-3">
          {d.discounted_price && <span className="font-display font-black text-2xl text-rose-400">${d.discounted_price}</span>}
          {d.original_price && <span className="text-zinc-500 line-through text-sm mb-1">${d.original_price}</span>}
        </div>
        {d.end_datetime && !scheduled && (
          <div className="mt-3 bg-black/40 rounded-lg p-2">
            <p className="text-[0.6rem] uppercase tracking-widest text-zinc-500 mb-1">Ends in</p>
            <Countdown to={d.end_datetime} endLabel="Expired" />
          </div>
        )}
        {scheduled && <p className="mt-2 text-xs text-amber-400">Starts {new Date(d.start_datetime).toLocaleDateString()}</p>}
        {left != null && <p className="text-[11px] text-zinc-500 mt-2">{soldOut ? "Sold out" : `${left} of ${d.quantity_available} left`}</p>}
        {isPromo ? (
          revealed ? (
            <div className="mt-3 space-y-2" data-testid={`promo-revealed-${d.id}`}>
              <div className="flex items-center gap-2 bg-black/50 border border-rose-500/40 rounded-lg p-2.5">
                <code className="flex-1 font-mono font-black text-base text-rose-300 tracking-widest text-center select-all" data-testid={`promo-code-${d.id}`}>{d.promo_code}</code>
                <button onClick={copyPromo} className="shrink-0 p-2 rounded-lg bg-rose-500 hover:bg-rose-600" title="Copy" data-testid={`promo-copy-${d.id}`}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <Link to="/marketplace" className="block text-center text-xs text-rose-400 hover:text-rose-300 font-bold" data-testid={`promo-shop-${d.id}`}>Shop now — apply at checkout →</Link>
            </div>
          ) : (
            <button onClick={() => setRevealed(true)} disabled={soldOut || scheduled} className="mt-3 w-full bg-rose-500 hover:bg-rose-600 font-bold py-2.5 rounded-lg text-sm disabled:opacity-40 flex items-center justify-center gap-1.5" data-testid={`reveal-promo-${d.id}`}>
              <Tag size={14} /> {soldOut ? "Sold out" : scheduled ? "Coming soon" : "Reveal Promo"}
            </button>
          )
        ) : claimed ? (
          <div className="mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-xs" data-testid="deal-claimed">
            <p className="text-emerald-300 font-bold">Claimed! Code: {claimed}</p>
            {d.redemption_instructions && <p className="text-zinc-400 mt-1">{d.redemption_instructions}</p>}
          </div>
        ) : showForm ? (
          <div className="mt-3 space-y-2">
            <input className={inp} placeholder="Your name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid={`claim-name-${d.id}`} />
            <input type="email" className={inp} placeholder="Your email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} data-testid={`claim-email-${d.id}`} />
            <button onClick={claim} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-2 rounded-lg text-sm" data-testid={`claim-confirm-${d.id}`}>Confirm claim</button>
          </div>
        ) : (
          <button onClick={() => setShowForm(true)} disabled={soldOut || scheduled} className="mt-3 w-full bg-rose-500 hover:bg-rose-600 font-bold py-2.5 rounded-lg text-sm disabled:opacity-40" data-testid={`claim-deal-${d.id}`}>
            {soldOut ? "Sold out" : scheduled ? "Coming soon" : "Claim deal"}
          </button>
        )}
        <Link to={`/deals/${d.slug || d.id}`} className="mt-2 block text-center text-[11px] text-zinc-400 hover:text-rose-400" data-testid={`deal-detail-link-${d.id}`}>View deal page →</Link>
        {d.terms && <p className="text-[0.6rem] text-zinc-600 mt-2 line-clamp-2">{d.terms}</p>}
      </div>
    </div>
  );
}

const MERCH_TYPE_BADGE = {
  event_ticket: "Ticket", attraction_ticket: "Ticket", activity_booking: "Booking",
  fan_club_membership: "Membership", venue_membership: "Membership", attraction_membership: "Membership",
  vip_experience: "VIP", gift_card: "Gift Card",
};

export function MerchCard({ m, testid }) {
  const badge = MERCH_TYPE_BADGE[m.product_type];
  const onSale = m.regular_price > 0 && m.price < m.regular_price;
  const inner = (
    <>
      <div className="relative">
        <img src={fileUrl(m.product_image)} alt={m.product_name} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500" />
        {badge && <span className="absolute top-2 left-2 bg-black/70 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-rose-300">{badge}</span>}
        {m.sold_out && <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-sm font-bold uppercase tracking-widest">Sold out</span>}
      </div>
      <div className="p-4">
        <p className="font-semibold text-sm">{m.product_name}</p>
        <p className="text-rose-400 font-bold flex items-center gap-1 mt-1">
          ${m.price}
          {onSale && <span className="text-zinc-500 line-through font-normal text-xs">${m.regular_price}</span>}
          {!m.marketplace_slug && <ExternalLink size={12} />}
        </p>
      </div>
    </>
  );
  const cls = "group bg-[#111] border border-white/10 rounded-2xl overflow-hidden card-hover block";
  return m.marketplace_slug ? (
    <Link to={`/marketplace/${m.marketplace_slug}`} className={cls} data-testid={testid}>{inner}</Link>
  ) : (
    <a href={m.external_link || "#"} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{inner}</a>
  );
}

export function MerchGrid({ items, title = "Merch" }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="merch-section">
      <p className="overline mb-5 flex items-center gap-2"><ShoppingBag size={14} /> {title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {items.map((m, i) => <MerchCard key={m.marketplace_slug || i} m={m} testid={`merch-item-${i}`} />)}
      </div>
    </section>
  );
}

export function TableBookingForm({ venueId }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", date: "", time: "", guests: "2", special_requests: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/table-bookings", { venue_id: venueId, ...f }); toast.success("Table request sent! The venue will confirm."); setF({ name: "", email: "", phone: "", date: "", time: "", guests: "2", special_requests: "" }); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section data-testid="table-booking">
      <p className="overline mb-5 flex items-center gap-2"><CalendarRange size={14} /> Reserve a table</p>
      <form onSubmit={submit} className="bg-[#111] border border-white/10 rounded-2xl p-6 grid sm:grid-cols-2 gap-3">
        <input required className={inp} placeholder="Name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tb-name" />
        <input required type="email" className={inp} placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="tb-email" />
        <input className={inp} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="tb-phone" />
        <input type="number" min="1" className={inp} placeholder="Guests" value={f.guests} onChange={(e) => set("guests", e.target.value)} data-testid="tb-guests" />
        <input required type="date" className={inp} value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="tb-date" />
        <input required type="time" className={inp} value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="tb-time" />
        <textarea rows={2} className={`${inp} sm:col-span-2`} placeholder="Special requests" value={f.special_requests} onChange={(e) => set("special_requests", e.target.value)} />
        <button disabled={busy} className="sm:col-span-2 bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40" data-testid="tb-submit">{busy ? "Sending..." : "Request reservation"}</button>
      </form>
    </section>
  );
}

export function PrivateEventForm({ venueId }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", event_type: "", requested_date: "", start_time: "", end_time: "", estimated_guests: "", budget_range: "", food_drink_needs: "", entertainment_needs: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/private-events", { venue_id: venueId, ...f }); toast.success("Private event request sent!"); setF({ name: "", email: "", phone: "", event_type: "", requested_date: "", start_time: "", end_time: "", estimated_guests: "", budget_range: "", food_drink_needs: "", entertainment_needs: "", message: "" }); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section data-testid="private-event">
      <p className="overline mb-5 flex items-center gap-2"><Tag size={14} /> Host a private event</p>
      <form onSubmit={submit} className="bg-[#111] border border-white/10 rounded-2xl p-6 grid sm:grid-cols-2 gap-3">
        <input required className={inp} placeholder="Name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pe-name" />
        <input required type="email" className={inp} placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="pe-email" />
        <input className={inp} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        <input className={inp} placeholder="Event type (birthday, corporate...)" value={f.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="pe-type" />
        <input type="date" className={inp} value={f.requested_date} onChange={(e) => set("requested_date", e.target.value)} data-testid="pe-date" />
        <input type="number" className={inp} placeholder="Estimated guests" value={f.estimated_guests} onChange={(e) => set("estimated_guests", e.target.value)} />
        <input type="time" className={inp} placeholder="Start" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} />
        <input type="time" className={inp} placeholder="End" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} />
        <input className={inp} placeholder="Budget range" value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)} />
        <input className={inp} placeholder="Food & drink needs" value={f.food_drink_needs} onChange={(e) => set("food_drink_needs", e.target.value)} />
        <input className={`${inp} sm:col-span-2`} placeholder="Entertainment needs" value={f.entertainment_needs} onChange={(e) => set("entertainment_needs", e.target.value)} />
        <textarea rows={2} className={`${inp} sm:col-span-2`} placeholder="Message" value={f.message} onChange={(e) => set("message", e.target.value)} />
        <button disabled={busy} className="sm:col-span-2 bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40" data-testid="pe-submit">{busy ? "Sending..." : "Send private event request"}</button>
      </form>
    </section>
  );
}
